/**
 * The document assembler: unresolved IR → resolved IR.
 *
 * This is the only stage that sees the whole document, and it does the
 * three things no individual block can: it numbers the sections, it
 * freezes every cross-reference into display text, and it fills the
 * attachment list. After this, the renderer needs no knowledge of
 * document structure at all — it walks nodes and emits markup.
 */
import { mapInlines } from '../ir/walk.js';
import { IR_SCHEMA, IR_VERSION, numberAffixes } from '../ir/nodes.js';
import { numberAttachments, numberSections } from './numbering.js';
export function assembleDocument(input, options = {}) {
    // The document says how it numbers itself; options are an override, so a
    // resolved IR assembles the same way wherever it is handled.
    const affixes = numberAffixes(input.numberStyle);
    const sectionSymbol = options.sectionSymbol ?? affixes.prefix;
    const sectionSuffix = options.sectionSuffix ?? affixes.suffix;
    const attachmentWord = options.attachmentWord ?? 'Bilaga';
    const diagnostics = [];
    // Deep-clone first: numbering stamps onto section nodes in place, and an
    // assembled document must never mutate its source.
    const document = structuredClone(input);
    const sections = new Map();
    const outline = [];
    for (const part of document.parts) {
        // Numbering restarts per part (ADR-0057) and per `restartNumbering`
        // scope, so a duplicate is only a duplicate within one scope.
        const numbersSeen = new Map();
        for (const { scope, ...entry } of numberSections(part.body)) {
            // Two clauses printing the same number is a legal defect, not a
            // cosmetic one — other clauses cross-reference these by number.
            if (entry.number != null) {
                const scopeKey = `${scope ?? ''}\u0000${entry.number}`;
                const owner = numbersSeen.get(scopeKey);
                if (owner) {
                    diagnostics.push({
                        severity: 'error',
                        code: 'duplicate_point_number',
                        message: `Two sections both print "${entry.number}" — "${owner}" and "${entry.ref}"${document.parts.length > 1 ? ` (part "${part.key}")` : ''}. A cross-reference to that number is ambiguous.`,
                    });
                }
                else {
                    numbersSeen.set(scopeKey, entry.ref);
                }
            }
            if (sections.has(entry.ref)) {
                diagnostics.push({
                    severity: 'error',
                    code: 'duplicate_section_ref',
                    message: `Section ref "${entry.ref}" is used more than once; references to it are ambiguous.`,
                });
            }
            sections.set(entry.ref, { number: entry.number, ...(entry.title ? { title: entry.title } : {}) });
            outline.push({ ...entry, part: part.key });
        }
    }
    const attachments = numberAttachments(document.attachments);
    const attachmentIndex = new Map(attachments.map((a) => [a.id, a]));
    const partTitles = new Map(document.parts.map((p) => [p.key, p.title]));
    const resolveInline = (node) => {
        switch (node.type) {
            case 'sectionRef': {
                const target = sections.get(node.target);
                if (!target) {
                    diagnostics.push({
                        severity: 'error',
                        code: 'unresolved_section_ref',
                        message: `Reference to unknown section "${node.target}".`,
                    });
                    return { ...node, text: `${sectionSymbol}?${sectionSuffix}` };
                }
                const number = target.number != null ? `${sectionSymbol}${target.number}${sectionSuffix}` : null;
                const display = node.display ?? 'number';
                if (display === 'title')
                    return { ...node, text: target.title ?? node.target };
                if (display === 'numberAndTitle') {
                    return { ...node, text: [number, target.title].filter(Boolean).join(' ') || node.target };
                }
                if (number == null) {
                    diagnostics.push({
                        severity: 'warning',
                        code: 'unnumbered_section_ref',
                        message: `Section "${node.target}" is unnumbered; referencing it by number falls back to its title.`,
                    });
                    return { ...node, text: target.title ?? node.target };
                }
                return { ...node, text: number };
            }
            case 'attachmentRef': {
                const target = attachmentIndex.get(node.target);
                if (!target) {
                    diagnostics.push({
                        severity: 'error',
                        code: 'unresolved_attachment_ref',
                        message: `Reference to undeclared attachment "${node.target}".`,
                    });
                    return { ...node, text: `${attachmentWord} ?` };
                }
                const numbered = target.number != null ? `${attachmentWord} ${target.number}` : attachmentWord;
                const display = node.display ?? 'numberAndLabel';
                if (display === 'label')
                    return { ...node, text: target.label };
                if (display === 'number')
                    return { ...node, text: numbered };
                return { ...node, text: `${numbered} (${target.label})` };
            }
            case 'documentRef': {
                const title = partTitles.get(node.target);
                if (!title) {
                    diagnostics.push({
                        severity: 'error',
                        code: 'unresolved_document_ref',
                        message: `Reference to unknown document part "${node.target}".`,
                    });
                    return { ...node, text: node.target };
                }
                return { ...node, text: title };
            }
            default:
                return node;
        }
    };
    const fillAttachmentLists = (list) => list.map((node) => {
        switch (node.type) {
            case 'attachmentList':
                return {
                    ...node,
                    entries: attachments.map((a) => ({ id: a.id, number: a.number, label: a.label })),
                };
            case 'section':
            case 'group':
            case 'notice':
                return { ...node, content: fillAttachmentLists(node.content) };
            case 'list':
                return { ...node, items: node.items.map((i) => ({ ...i, content: fillAttachmentLists(i.content) })) };
            case 'optionList':
                return {
                    ...node,
                    items: node.items.map((i) => (i.children ? { ...i, children: fillAttachmentLists(i.children) } : i)),
                };
            case 'table':
                return {
                    ...node,
                    rows: node.rows.map((r) => ({ ...r, cells: r.cells.map((c) => ({ ...c, content: fillAttachmentLists(c.content) })) })),
                };
            case 'detailList':
                return { ...node, items: node.items.map((i) => ({ ...i, description: fillAttachmentLists(i.description) })) };
            default:
                return node;
        }
    });
    const resolvePart = (list) => mapInlines(fillAttachmentLists(list), resolveInline);
    const resolved = {
        schema: IR_SCHEMA,
        version: IR_VERSION,
        locale: document.locale,
        title: document.title,
        ...(document.numberStyle ? { numberStyle: document.numberStyle } : {}),
        manifest: document.manifest,
        parts: document.parts.map((part) => ({
            ...part,
            ...(part.letterhead ? { letterhead: resolvePart(part.letterhead) } : {}),
            body: resolvePart(part.body),
            ...(part.footer ? { footer: resolvePart(part.footer) } : {}),
        })),
        attachments,
        outline,
        resolved: true,
    };
    return { document: resolved, diagnostics };
}
//# sourceMappingURL=assemble.js.map